<template>
  <div class="settings-indic">
    <div
      v-if="indicator.copyPublic !== null"
      v-quick-title="$t('message.INDIC_PUBLIC_FROM_OTHER_TEAM')"
      class="a-from-support h-v-centerer"
    >
      <div class="triangle" />
      <div class="text">
        {{ $t("message.PUBLIC_INDIC_LETTER") }}
      </div>
    </div>
    <div
      v-else-if="indicator.isGw === 1"
      v-quick-title="$t('message.INDIC_SETTINGS_IS_GW')"
      class="a-from-support h-v-centerer from-gw"
    >
      <div class="triangle" />
      <Icon>{{ hardhatExtraBold }}</Icon>
    </div>
    <div class="settings-indic__title v-centerer" @click="indicator.expanded = !indicator.expanded">
      <DraggingIndication />
      <Icon :rotate-left="!indicator.expanded" class="indic-arrow">
        {{ arrowDownLine }}
      </Icon>
      <div class="indic-name">
        <EditableLabel v-model="indicator.name" generic-border @input="$emit('edit', indicator, 'name')" />
      </div>
      <label :for="`indic-unit-${indicator.id}`">
        {{ $t("message.INDIC_UNIT") + $t('message.TWO_DOTS') }}
      </label>
      <div class="indic-unit">
        <EditableLabel v-model="indicator.unit" generic-border @input="$emit('edit', indicator, 'unit')" />
      </div>

      <div class="settings-indic__title__library field-hint">
        {{ $t('message.INDIC_LIB_NAME', { name: decodeHTML(indicator.libName) }) }}
      </div>
      <div
        class="generic-border generic-border--icon-button generic-border--red settings-indic__title__delete"
        @click.stop="$emit('delete')"
      >
        <Icon>{{ deleteMedium }}</Icon>
      </div>
    </div>

    <div v-if="indicator.expanded || alwaysExpanded" class="indic-details">
      <div
        v-if="indicator.isGw === 1 && indicator.gwItems"
        class="settings-indic__usage field-hint"
      >
        <span class="settings-indic__usage__content" v-html="usageInfo" />
        <button v-if="indicator.gwItems.length" class="option-button" @click="fullUsage = !fullUsage">
          {{ $t('message.SEE_MORE') }}
        </button>
      </div>
      <div class="columns-wrapper">
        <!-- Objective -->
        <div>
          <div class="section-legend">
            {{ $t("message.DEFINE_MIN_MAX") }}
          </div>
          <RadioGroup
            v-model="indicator.objectiveIsMax"
            style="display: inline-block;"
            :items="radioObjItems"
            :disabled="indicator.copyPublic !== null"
            @input="$emit('edit', indicator, 'objectiveIsMax')"
          />
          <InputNumber
            v-model="indicator.objective"
            class="objective-wrapper"
            :class="{ top: indicator.objectiveIsMax != 1 }"
            :disabled="indicator.copyPublic !== null"
            @input="tempEditObjective"
          />
          <div class="field-hint mt5">
            {{ objectiveHint }}
          </div>
        </div>
        <!-- Line / bars -->
        <div>
          <div class="section-legend">
            {{ $t("message.DEFINE_VISUAL_TYPE") }}
          </div>
          <RadioGroup
            v-model="indicator.type"
            style="display: inline-block;"
            :items="chartTypeItems"
            :disabled="indicator.copyPublic !== null"
            @input="$emit('edit', indicator, 'type')"
          />
        </div>
        <!-- Select the value input interval and allow calculating average -->
        <div>
          <div class="section-legend align-top" v-quick-title="$t('message.INDIC_INPUT_SETTINGS_HELP')">
            <span>{{ $t("message.INDIC_INPUT_INTERVAL_SETTINGS") }}</span>
            <HelperButton :content="$t('message.INDIC_INPUT_SETTINGS_HELP')" />
          </div>
          <div class="input-period">
            <div>
              <span>
                {{ $t('message.IDC_POINT_PERIOD') + $t('message.TWO_DOTS') }}
              </span>
              <InputNumber
                v-if="false"
                v-model="indicator.inputPeriod"
                :min="1"
                @input="$emit('edit', indicator, 'inputPeriod')"
              />
              <EditableLabelRadio
                :value="inputUnitId"
                class="ml5"
                :items="inputPeriodItems"
                label-key="unit"
                :disabled="indicator.copyPublic !== null"
                @input="inputPeriodUnitSet"
              />
            </div>
            <div class="average">
              <span>
                {{ $t('message.OVER_LONGER_PERIOD') + $t('message.TWO_DOTS') }}
              </span>
              <EditableLabelRadio
                v-model="indicator.isAverage"
                class="ml5"
                :items="averageItems"
                :disabled="indicator.copyPublic !== null"
                @input="$emit('edit', indicator, 'isAverage')"
              />
              <div v-if="indicator.isAverage == 1 && calculatedIndicator" class="ml10em mt3em">
                <!-- This setting's documentation can be found in indicators-settings-item.readme.md -->
                <IntCheckbox
                  v-model="indicator.averageAfterCalculation"
                  :title="$t('message.SETTINGS_INDIC_AVERAGE_AFTER_CALC')"
                  :disabled="indicator.copyPublic !== null"
                  @input="$emit('edit', indicator, 'averageAfterCalculation')"
                />
                <div class="field-hint">
                  {{ $t('message.SETTINGS_INDIC_AVERAGE_AFTER_CALC_HINT') }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Static, incremental, public -->
        <div>
          <div v-quick-title="specificSettingsHelp" class="section-legend align-top">
            <span>{{ $t("message.SPECIFIC_SETTINGS") }}</span>
            <HelperButton :content="specificSettingsHelp" />
          </div>
          <IntCheckbox
            v-model="indicator.static"
            v-quick-title="calculatedIndicator ? $t('message.SETTINGS_INDIC_CALC_NOT_STATIC') : null"
            :title="$t('message.SETTINGS_STATIC_VALUES')"
            :disabled="indicator.copyPublic !== null || calculatedIndicator"
            @input="$emit('edit', indicator, 'static')"
          />
          <br>
          <div class="align-top">
            <Checkbox
              v-quick-title="calculatedIndicator ? $t('message.SETTINGS_INDIC_CALC_NOT_STATIC') : null"
              :title="$t('message.SETTINGS_INCREMENTAL_VALUES')"
              :value="indicator.incremental !== 0"
              :disabled="indicator.copyPublic !== null || calculatedIndicator"
              @input="incrementalCheckboxInput"
            />
            <InputNumber
              :ref="`incrementalInput${indicator.id}`"
              v-model="indicator.incremental"
              v-quick-title="calculatedIndicator ? $t('message.SETTINGS_INDIC_CALC_NOT_STATIC') : null"
              class="mt5"
              :disabled="indicator.copyPublic !== null || calculatedIndicator"
              @input="onIncrementalInput($event)"
            />
            <div class="small-checkbox" :class="{ expanded: isIncremental }">
              <IntCheckbox
                v-model="indicator.incrementNonWD"
                :title="$t('message.SETTINGS_INCREMENT_NON_WD')"
                :disabled="indicator.copyPublic !== null"
                @input="$emit('edit', indicator, 'incrementNonWD')"
              />
            </div>
          </div>
          <IntCheckbox
            v-model="indicator.isPublic"
            :title="$t('message.SETTINGS_PUBLIC_INDIC')"
            :disabled="indicator.copyPublic !== null"
            @input="$emit('edit', indicator, 'isPublic')"
          />
          <br>
          <Checkbox
            v-if="currentTeam && currentTeam.accessGW"
            v-model="isIndicatorGw"
            :title="$t('message.INDIC_SETTINGS_GW_INDICATOR')"
            :disabled="indicator.copyPublic !== null"
          />
          <div class="calculated-indicator-checkbox v-centerer">
            <Checkbox
              v-if="!calculationDisabled"
              v-model="calculatedIndicator"
              v-quick-title="staticOrIncremental ? $t('message.SETTINGS_INDIC_CALC_NOT_STATIC') : null"
              :title="$t('message.SETTINGS_CALCULATED_INDIC')"
              :disabled="indicator.copyPublic !== null || staticOrIncremental"
              @input="calcCheckboxInput"
            />
          </div>
          <IntCheckbox
            v-model="indicator.adaptativeValuesRange"
            :title="$t('message.INDIC_SETTINGS_ADAPTATIVE_RANGE')"
            @input="$emit('edit', indicator, 'adaptativeValuesRange')"
          />
        </div>

        <!-- Calculation expression if any -->
        <div v-if="calculatedIndicator && !calculationDisabled">
          <div v-quick-title="$t('message.SETTINGS_INDICATOR_CALC_HINT')" class="section-legend align-top">
            <span>{{ $t("message.SETTINGS_INDICATOR_CALC_EXPR") }}</span>
            <HelperButton :content="$t('message.SETTINGS_INDICATOR_CALC_HINT')" />
          </div>
          <div class="field-hint">
            {{ $t("message.SETTINGS_INDICATOR_CALC_SMALL_HINT") }}
          </div>
          <div class="indic-calc-field">
            <div
              ref="contenteditable"
              contenteditable
              @keydown="calcFieldKeyDown"
            >
              <span
                v-for="(calcKey, index) in indicator.calcItems"
                ref="calcKey"
                :key="index"
                v-quick-title="calcKey.calculationKey === 'indicator-value' ? calcKey.teamName : null"
                contenteditable="false"
                class="indic-calc-field-key"
                :class="expressionItemClass(calcKey)"
                @click="calcKeyClicked(calcKey, index, $event)"
              >{{
                expressionItemDisplayedString(calcKey, { themes: allThemes })
              }}</span>
            </div>
          </div>
          <div v-if="calcExpressionInvalid" class="indic-calc-invalid">
            {{ $t('message.SETTINGS_INDICATOR_CALC_EXPR_INVALID') }}
          </div>

          <div class="indic-calc-rows">
            <div v-for="(row, rowIndex) in CALC_ROWS" :key="rowIndex" class="indic-calc-row">
              <div
                v-for="key in row.items"
                :key="key.displayedString"
                class="indic-calc-key"
                :class="{ ...row.keyClass }"
                @mousedown="setCalculationCaretPosition"
                @click="addCalcItem(key)"
              >
                {{ key.displayedStringTr ? $t(key.displayedStringTr) : key.displayedString }}
              </div>
            </div>
          </div>

          <IntCheckbox
            v-model="indicator.displayCalcComments"
            :title="$t('message.INDIC_SETTINGS_DISPLAY_CALC_COMMENTS')"
            :disabled="indicator.copyPublic !== null"
            @input="$emit('edit', indicator, 'displayCalcComments')"
          />

          <!-- When adding an indicator, choose the indicator -->
          <AdvancedTeamPicker
            v-if="selectingCalcIndicTeam"
            :value="[calcIndicatorRadioTeamId || currentTeam.id]"
            display-all-teams
            invisible
            ok-button
            single-value
            edit-mode-always-on
            disable-inaccessible
            @input="calcIndicTeamPicked"
            @endEdit="selectingCalcIndicTeam = false"
          />
          <EditableLabelRadio
            ref="calcIndicatorRadio"
            :value="defaultCalcIndicValue"
            :items="calcAddingIndicatorChoicesPerTeam[`${calcIndicatorRadioTeamId}`]"
            :category-level="1"
            label-key="name"
            no-text
            :none-string="$t('message.NO_INDICATOR')"
            @input="calcAddingIndicatorInput"
            @close="defaultCalcIndicValue = 0"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { evaluate } from 'mathjs';
import { cloneDeep } from 'lodash';
// Vuex utils
import { mapState } from 'vuex';
import { FETCH_TEAMS } from '@/store/action-types';
import { GET_TEAMS } from '@/store/mutation-types';
// Components
import AdvancedTeamPicker from '@/components/advanced-team-picker.vue';
import Checkbox from '@/components/checkbox.vue';
import DraggingIndication from '@/components/dragging-indication.vue';
import EditableLabel from '@/components/editable-label.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import HelperButton from '@/components/helper-button.vue';
import Icon from '@/components/icon.vue';
import InputNumber from '@/components/input-number.vue';
import IntCheckbox from '@/components/int-checkbox.vue';
import RadioGroup from '@/components/radio-group.vue';
// Utils
import {
  arrowDownLine,
  deleteMedium,
  hardhatExtraBold,
} from '@/assets/icons';
import { calert, decodeHTML, nestedFindItemWithId, PERIOD_UNITS_DATA } from '@/utils/main';
import {
  CALC_ROWS,
  expressionItemClass,
  expressionItemDisplayedString,
  formattedIndicatorExpression,
} from '@/utils/indicator-utils';

export default {

  name: 'IndicatorsSettingsItem',

  components: {
    AdvancedTeamPicker,
    Checkbox,
    DraggingIndication,
    EditableLabel,
    EditableLabelRadio,
    HelperButton,
    Icon,
    InputNumber,
    IntCheckbox,
    RadioGroup,
  },

  props: {
    indicator: {
      type: Object,
      required: true,
    },
    allThemes: {
      type: Array,
      required: true,
    },
    calculationDisabled: Boolean,
    alwaysExpanded: Boolean,
  },

  data() {
    return {
      arrowDownLine,
      deleteMedium,
      hardhatExtraBold,
      radioObjItems: [
        { id: 0, label: this.$t('message.MINIMUM') },
        { id: 1, label: this.$t('message.MAXIMUM') },
      ],
      chartTypeItems: [
        { id: 0, label: this.$t('message.CHART_LINE') },
        { id: 1, label: this.$t('message.CHART_BAR') },
      ],
      averageItems: [
        { id: 0, label: this.$t('message.CUMULATE') },
        { id: 1, label: this.$t('message.CALC_AVERAGE') },
      ],
      created: false,
      calcEditTimeout: null,
      calculatedIndicator: false,
      calcExpressionItems: [],
      calcIndicatorRadioTeamId: null,
      selectingCalcIndicTeam: false,
      editingCalcKey: null,
      CALC_ROWS,
      fullUsage: false,
      calculationFieldPosition: null,
      defaultCalcIndicValue: 0,
    };
  },

  computed: {
    /** As of v0.3.46, objective may be confusing when using a long-term indicator */
    objectiveHint() {
      const { inputPeriodUnit } = this.indicator;
      const trans = { d: 'N_DAYS', w: 'N_WEEKS', M: 'N_MONTHS' };
      const period = this.$tc(`message.${trans[inputPeriodUnit]}`, 1);
      return this.$t('message.INDIC_SETTINGS_OBJ_HINT', { period });
    },
    /** Input interval unit id (for EditableLabelRadio) */
    inputUnitId() {
      return PERIOD_UNITS_DATA.findIndex(item => item.letter === this.indicator.inputPeriodUnit);
    },
    /** Input interval available units (for EditableLabelRadio) */
    inputPeriodItems() {
      return PERIOD_UNITS_DATA.map(item => ({ ...item, unit: this.$tc(item.unitTr, this.indicator.inputPeriod) }));
    },
    /** Array: All indicators formatted for an EditableLabelRadio */
    allRadioThemes() {
      return this.allThemes.map(theme => ({ ...theme, radioItems: theme.indicators }));
    },
    /** Boolean: True if the expression cannot be resolved */
    calcExpressionInvalid() {
      try {
        formattedIndicatorExpression(this.indicator.calcItems, 1);
        evaluate(formattedIndicatorExpression(this.indicator.calcItems, 1));
      } catch (e) {
        return true;
      }
      return false;
    },
    calcAddingIndicatorChoicesPerTeam() {
      const teams = [this.currentTeam, ...this.$store.getters.allAccessibleTeams];
      return teams.reduce((all, team) => {
        const themes = this.$store.state.indicatorsThemesPerTeam[`${team.id}`] || [];
        return {
          ...all,
          [`${team.id}`]: themes.map(theme => ({
            ...theme,
            title: theme.name,
            radioItems: theme.indicators.filter(indic => indic.id !== this.indicator.id),
          })),
        };
      }, {});
    },
    /** Array: All themes available as calculated indicators */
    calcAllThemes() {
      const teams = [this.currentTeam, ...this.$store.getters.allAccessibleTeams];
      return teams.flatMap(team => this.$store.state.indicatorsThemesPerTeam[`${team.id}`] || []);
    },
    /** Boolean: Whether the indicator is static or incremental */
    staticOrIncremental() {
      return this.indicator.static === 1 || this.isIncremental;
    },
    /** Boolean: Whether the indicator is incremental */
    isIncremental() {
      return this.indicator.incremental > 0;
    },
    /** String: Hints about specific settings usage */
    specificSettingsHelp() {
      const gwHint = this.currentTeam && this.currentTeam.accessGW
        ? `${this.$t('message.INDIC_SETTINGS_GW_HINT')}<br><br>`
        : '';
      return this.$t('message.INDIC_SPEC_SETTINGS_HELP', { gwHint });
    },
    /** Boolean: Whether the indicator is only used for field tours */
    isIndicatorGw: {
      get() {
        return this.indicator.isGw === 1;
      },
      set(newVal) {
        this.$emit('edit', this.indicator, 'isGw');
      },
    },
    /** String: HTML string used to display where this indicator is used */
    usageInfo() {
      if (this.indicator.gwItems.length === 0) {
        return this.$t('message.INDIC_SETTINGS_GW_USAGE_NOT');
      }
      const { gwItems } = this.indicator;
      const nTimes = this.$tc('message.N_TIMES', gwItems.length, { nb: gwItems.length });
      let content = this.$t('message.INDIC_SETTINGS_GW_USAGE', { nTimes });
      if (this.fullUsage) {
        content += gwItems.reduce(
          (text, item) => `${text}<br> - ${decodeHTML(item.gwName)} (${decodeHTML(item.checklistName)})`, '',
        );
      }
      return content;
    },
    /** Array: Merged available calculation keys */
    allCalcKeys() {
      return CALC_ROWS.reduce((allKeys, row) => [...allKeys, ...row.items], []);
    },
    ...mapState({
      allTeamsLoading: state => state[GET_TEAMS.loadingKey],
    }),
  },

  created() {
    if (!this.allTeamsLoading) {
      this.$store.dispatch(FETCH_TEAMS);
    }
    this.calculatedIndicator = this.indicator.calcItems && this.indicator.calcItems.length > 0;
    this.$nextTick(() => { this.created = true; });
  },

  beforeDestroy() {
    clearTimeout(this.objectiveTimeout);
    clearTimeout(this.calcEditTimeout);
  },

  methods: {
    decodeHTML,
    expressionItemClass,
    expressionItemDisplayedString,
    /** Set a timeout to the objective edition */
    tempEditObjective() {
      clearTimeout(this.objectiveTimeout);
      this.objectiveTimeout = setTimeout(() => {
        this.$emit('edit', this.indicator, 'objective');
      }, 250);
    },
    incrementalCheckboxInput(val) {
      this.indicator.incremental = val ? 1 : 0;
      this.$emit('edit', this.indicator, 'incremental');
    },
    /** Triggered when incremental field is changed */
    onIncrementalInput() {
      this.$emit('edit', this.indicator, ['incrementNonWD', 'incremental']);
    },
    /** Input interval unit set */
    inputPeriodUnitSet(value) {
      // Restrict if any other indicator uses this one to calculate its values
      // Or if the current indicator uses any other indicator
      if ((this.indicator.calcItems || []).some(item => item.calculatedIndicator !== null)) {
        calert(this.$t('message.SETTINGS_CALC_WRONG_PERIOD_UNIT'));
        return;
      }
      for (let i = 0, themesLen = this.allThemes.length; i < themesLen; i++) {
        const theme = this.allThemes[i];
        for (let j = 0, indicsLen = theme.indicators.length; j < indicsLen; j++) {
          const indic = theme.indicators[j];
          if ((indic.calcItems || []).some(item => item.calculatedIndicator === this.indicator.id)) {
            calert(this.$t('message.SETTINGS_CALC_INDIC_USED_PERIOD_UNIT', { name: indic.name }));
            return;
          }
        }
      }

      const { letter } = PERIOD_UNITS_DATA[value];
      this.indicator.inputPeriodUnit = letter;
      if (letter !== 'd') {
        // If input is not daily, cannot be incremental or static
        this.indicator.incremental = 0;
        this.indicator.static = 0;
      }
      this.$emit('edit', this.indicator, ['inputPeriodUnit', 'static', 'incremental']);
    },
    /** Event on checking/unchecking the 'calculatedIndicator' Checkbox */
    calcCheckboxInput(isCalculated) {
      if (!isCalculated) {
        // Remove the calculation option => remove calc items
        this.indicator.calcItems = [];
        this.emitCalcItemsUpdate();
        if (this.indicator.displayCalcComments === 1) {
          this.indicator.displayCalcComments = 0;
          this.$emit('edit', this.indicator, ['displayCalcComments']);
        }
      }
    },
    /** Detect input in the calculation field */
    calcFieldKeyDown(event) {
      const range = window.getSelection().getRangeAt(0);
      const position = range.startOffset;
      const key = event.key === ',' ? '.' : event.key;
      if (key === 'Backspace') {
        if (position > 0) {
          this.deleteCalcItem(position - 1);
        }
      } else if (key === 'Delete') {
        this.deleteCalcItem(position);
      } else if (
        key === '+'
        || key === '-'
        || isFinite(key)
        || key === '*'
        || key === '/'
        || key === '.'
        || key === '('
        || key === ')'
      ) {
        const item = this.allCalcKeys.find(refKey => key === (refKey.calculationKey || refKey.displayedString));
        if (item) {
          this.addCalcItem(item);
        }
      } else if (key === 'ArrowLeft') {
        this.setCalcCaretAfterPosition(position - 2);
      } else if (key === 'ArrowRight') {
        this.setCalcCaretAfterPosition(position);
      }
      // Prevent native field interaction
      event.preventDefault();
    },
    /** Detect if the calculation field was focused when clicking a key */
    setCalculationCaretPosition() {
      if (document.activeElement === this.$refs.contenteditable) {
        const sel = window.getSelection();
        if (sel.anchorNode !== null) {
          const range = window.getSelection().getRangeAt(0);
          this.calculationFieldPosition = range.startOffset;
        }
      }
    },
    /** Adding a calculation expression part */
    addCalcItem(item) {
      // Add an indicator into the calculation
      if (item.calculationKey === 'indicator-value') {
        if (item.pickTeam) {
          this.selectingCalcIndicTeam = true;
        } else {
          this.calcIndicatorRadioTeamId = this.currentTeam.id;
          this.$refs.calcIndicatorRadio.edit();
        }
        return;
      }
      let position = this.indicator.calcItems.length;
      this.setCalculationCaretPosition();
      if (this.calculationFieldPosition !== null) {
        // The field is or was just focused. Either the character was typed directly
        // in the field, or a button was clicked while the field was focused. So get the position of the cursor
        position = this.calculationFieldPosition;
      }

      const el = { calculationKey: item.calculationKey === undefined ? item.displayedString : item.calculationKey };
      this.indicator.calcItems.splice(position, 0, el);

      // Set the caret position after the newly added item
      this.$nextTick(() => {
        this.setCalcCaretAfterPosition(position);
      });
      this.calculationFieldPosition = null;
      this.emitCalcItemsUpdate();
    },
    onOrderChange(newList) {
      this.indicator.calcItems = newList;
      this.emitCalcItemsUpdate();
    },
    /** User picked the team from which he wishes to pick an indicator */
    calcIndicTeamPicked(teams) {
      this.calcIndicatorRadioTeamId = teams[0];
      if (this.calcIndicatorRadioTeamId) {
        this.$nextTick(this.$refs.calcIndicatorRadio.edit);
      }
      this.selectingCalcIndicTeam = false;
    },
    /** Adding a calculation expression part after picking an indicator */
    calcAddingIndicatorInput(indicatorId) {
      this.defaultCalcIndicValue = 0;
      const indicator = nestedFindItemWithId(this.calcAllThemes, indicatorId, 'indicators', true);
      if (indicator.inputPeriodUnit !== this.indicator.inputPeriodUnit) {
        calert(this.$t('message.SETTINGS_CALC_WRONG_PERIOD_UNIT'));
        return;
      }
      if (this.editingCalcKey === null) {
        // User needed to add a new item to the calculation
        let position = this.indicator.calcItems.length;
        if (this.calculationFieldPosition !== null) {
          position = this.calculationFieldPosition;
        }
        const indicItem = {
          calculationKey: 'indicator-value',
          calculatedIndicator: indicatorId,
        };
        this.indicator.calcItems.splice(position, 0, indicItem);
        this.calculationFieldPosition++;
      } else {
        // User needed to edit one indicator item of the calculation
        this.editingCalcKey.calculatedIndicator = indicatorId;
      }
      this.emitCalcItemsUpdate();
      let position = this.indicator.calcItems.length;
      if (this.calculationFieldPosition !== null) {
        // The field is or was just focused. Either the character was typed directly
        // in the field, or a button was clicked while the field was focused. So get the position of the cursor
        position = this.calculationFieldPosition;
      }
      this.$nextTick(() => {
        this.setCalcCaretAfterPosition(position - 1);
        this.calculationFieldPosition = null;
      });
      this.editingCalcKey = null;
    },
    /** Handle click on an item of the expression */
    calcKeyClicked(calcKey, index, event) {
      if (calcKey.calculationKey === 'indicator-value' && calcKey.calculatedIndicator > 0) {
        // Check which team owns that indicator
        const indicId = calcKey.calculatedIndicator;
        const indicator = nestedFindItemWithId(this.calcAllThemes, indicId, 'indicators', true);
        if (!indicator) {
          return;
        }
        // Display the edition modal
        this.calcIndicatorRadioTeamId = indicator.team;
        this.$refs.calcIndicatorRadio.edit();
        this.defaultCalcIndicValue = indicId;
        this.editingCalcKey = calcKey;
        this.setCalculationCaretPosition();
      }
      // Get where the user clicked with his mouse on the calculation key
      const rect = event.target.getBoundingClientRect();
      const x = event.clientX - rect.left; // x position within the element.
      const width = event.currentTarget.offsetWidth;

      // Set cursor at span's position
      if (x < width / 2) {
        this.setCalcCaretAfterPosition(index - 1);
      } else {
        this.setCalcCaretAfterPosition(index);
      }
    },
    /** The the caret position after the item n° position */
    setCalcCaretAfterPosition(position) {
      if (this.$refs.calcKey && position < this.$refs.calcKey.length && position >= -1) {
        const el = this.$refs.contenteditable;
        const range = document.createRange();
        const sel = window.getSelection();
        range.setStart(el, position + 1);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    },
    /** Remove an item of the expression */
    deleteCalcItem(index) {
      this.indicator.calcItems.splice(index, 1);
      this.setCalcCaretAfterPosition(index - 1);
      this.indicator.calcItems = this.indicator.calcItems;
      this.emitCalcItemsUpdate();
    },
    emitCalcItemsUpdate() {
      const calcItems = cloneDeep(this.indicator.calcItems);
      this.$emit('edit', this.indicator, 'calcItems', { calcItems });
    },
  },
};
</script>

<style lang="scss">

@import "@/assets/scss/vars.scss";

.settings-indic {
  border-radius: $main-border-radius;

  .indic-details {
    padding-left: 40px;
    padding-bottom: 0.5em;

    .columns-wrapper {
      display: flex;
      flex-flow: row wrap;
      align-items: stretch;

      > div {
        flex: 0 0 auto;
        margin-left: 35px;
        position: relative;
        max-width: calc(100% - 35px);

        &::before {
          content: " ";
          width: 3px;
          background-color: rgba(0, 0, 0, 0.1);
          display: inline-block;
          position: absolute;
          top: 1em;
          bottom: 1em;
          left: -17px;
        }
      }
    }

    .input-period {
      margin-top: 5px;

      > * {
        vertical-align: middle;
      }
      input, .editable-label {
        border: 1px gray dashed;
        padding: 0px 5px;
        line-height: 1.5em;
        background: #fff;
        font-size: 1em;
      }
      input {
        padding: 0.5px 5px;
        width: 30px;
        margin: 0px;
      }
      .editable-label {
        display: inline-block;
      }
    }
    input {
      margin-left: 20px;
    }
    .min-max {
      margin-top: 5px;
    }
    .section-legend {
      color: #2A5A91;
      font-weight: 500;
      margin-top: 0.5em;
      font-style: italic;
    }
  }

  .indic-calc-field {
    background: white;
    border: 1px solid rgba(0, 0, 0, 0.2);
    line-height: 1.4em;
    margin-top: 0.2em;
    display: flex;
    align-items: center;
    padding: 0.15em 0.3em;

    & > div {
      width: 100%;
    }
  }
  .indic-calc-invalid {
    font-style: italic;
    font-size: 0.9em;
    color: $color-red-bad;
  }
  .indic-calc-field-key {
    // Disabling the selection of multiple keys in the calculated indicator input
    user-select: none;
    cursor: text;
    margin-right: 5px;
    font-size: 0.85em;

    &.orange, &.blue {
      padding: 0 0.1em;
      margin: 0 0.1em;
      border-radius: 0.2em;
    }

    &.orange {
      background-color: rgba(189, 87, 0, 0.15);
      border: 1px solid rgba(189, 87, 0, 0.25);
    }

    &.gray {
      padding: 0;
      margin: 0;
    }

    &.blue {
      background-color: rgba(12, 0, 224, 0.15);
      border: 1px solid rgba(12, 0, 224, 0.25);

      &:hover {
        background-color: rgba(12, 0, 224, 0.25);
        border: 1px solid rgba(12, 0, 224, 0.35);
      }
    }
  }
  .indic-calc-row {
    display: flex;
    margin: 0.2em 0;

    &:first-child {
      margin-top: 0.3em;
    }

    .indic-calc-key {
      background: rgba(0, 0, 0, 0.1);
      border: 1px solid rgba(0, 0, 0, 0.2);
      margin: 0px 0.3em;
      padding: 0.1em 0.4em;
      border-radius: 0.2em;
      transition: background .1s;
      cursor: pointer;

      &:hover {
        background: rgba(0, 0, 0, 0.2);
        border: 1px solid rgba(0, 0, 0, 0.3);
      }

      &.orange {
        background-color: rgba(189, 87, 0, 0.15);
        border: 1px solid rgba(189, 87, 0, 0.25);

        &:hover {
          background: rgba(189, 87, 0, 0.25);
          border: 1px solid rgba(189, 87, 0, 0.35);
        }
      }
      &.blue {
        background-color: rgba(12, 0, 224, 0.15);
        border: 1px solid rgba(12, 0, 224, 0.25);

        &:hover {
          background-color: rgba(12, 0, 224, 0.25);
          border: 1px solid rgba(12, 0, 224, 0.35);
        }
      }
    }
  }

  &__usage {
    padding: 0.5em 0 0.2em 0;
  }

  &__title {
    padding: 0.5em 0;
    cursor: pointer;

    &__library {
      flex: 1;
      min-width: 0px;
      margin-left: 10px;
      width: calc(50% - 200px);
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      text-align: right;
    }

    &__delete {
      margin-right: 1.5rem;
      transform: scale(0.8);
    }

    .indic-name, .indic-unit {
      margin: 0px 10px;
      padding: 2px 5px;
      overflow: hidden;
    }

    .indic-name {
      margin-right: 5%;
      width: 30%;
    }

    .indic-unit {
      width: 10%;
    }

    .indic-arrow {
      margin-left: 5%;
      transition: transform 0.2s ease-in-out;
    }

    .dragging-indication {
      left: 1.5rem;
      transform: translateY(-50%) scale(0.8);
    }
  }
}

</style>
